import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Star, Truck, RefreshCw, ShieldCheck, Minus, Plus } from "lucide-react";
import api, { inr } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { FadeUp } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import QuickView from "../components/QuickView";
import RichText from "../components/RichText";
import EditableImage from "../components/admin/EditableImage";
import EditableText from "../components/admin/EditableText";
import ImageUpload from "../components/ImageUpload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Pencil } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";

function Zoom({ src, alt }) {
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [zoom, setZoom] = useState(false);
  const ref = useRef(null);
  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  return (
    <div
      ref={ref}
      className="aspect-[3/4] overflow-hidden bg-[#efece6] cursor-zoom-in"
      onMouseEnter={() => setZoom(true)}
      onMouseLeave={() => setZoom(false)}
      onMouseMove={move}
      data-testid="product-zoom"
    >
      <img
        src={src}
        alt={alt}
        className="h-full w-full object-cover transition-transform duration-300"
        style={{ transform: zoom ? "scale(1.9)" : "scale(1)", transformOrigin: `${pos.x}% ${pos.y}%` }}
      />
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { addToCart, toggleWishlist, inWishlist } = useStore();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [recent, setRecent] = useState([]);
  const [quickView, setQuickView] = useState(null);
  const [qvOpen, setQvOpen] = useState(false);
  const [imgEditOpen, setImgEditOpen] = useState(false);
  const [imgDraft, setImgDraft] = useState("");
  const [notifyMode, setNotifyMode] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [reviews, setReviews] = useState([]);

  useEffect(() => { setReviews(product?.reviews || []); }, [product?.reviews]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSize(null); setColor(""); setQty(1); setActive(0); setNotifyMode(false); setNotifyEmail("");
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      const v = r.data.color_variants || [];
      if (v.length) setColor(v[0].color);
      // recently viewed
      const rv = JSON.parse(localStorage.getItem("ambrai_recent") || "[]").filter((p) => p.slug !== slug);
      const updated = [{ slug: r.data.slug, name: r.data.name, price: r.data.price, primary_image: r.data.primary_image, hover_image: r.data.hover_image, fabric: r.data.fabric, sizes: r.data.sizes, compare_at_price: r.data.compare_at_price, id: r.data.id, stock: r.data.stock, stock_by_size: r.data.stock_by_size, is_new: r.data.is_new, on_sale: r.data.on_sale }, ...rv].slice(0, 8);
      localStorage.setItem("ambrai_recent", JSON.stringify(updated));
      setRecent(rv.slice(0, 4));
    });
  }, [slug]);

  if (!product) {
    return <div className="pt-40 pb-40 text-center font-serif text-3xl">Loading…</div>;
  }

  const saved = inWishlist(product.slug);
  const openQuickView = (p) => { setQuickView(p); setQvOpen(true); };
  const isAdmin = user?.role === "admin";

  const savePatch = async (patch) => {
    const payload = {
      ...product,
      color: (product.colors && product.colors[0]) || "",
      ...patch,
    };
    const { data } = await api.put(`/admin/products/${product.id}`, payload);
    // Merge into current product to preserve derived fields (related, reviews) that
    // are only present on the public GET /products/{slug} response.
    setProduct((prev) => ({ ...prev, ...data }));
  };

  const saveReviews = (list) => { setReviews(list); savePatch({ reviews: list }); };

  const handleAdd = () => {
    if (!size) { toast.error("Please select a size"); return; }
    if (hasColors && !color) { toast.error("Please select a colour"); return; }
    if (hasColors ? colorStock <= 0 : sizeStock(size) <= 0) { toast.error(hasColors ? "This colour is sold out" : "This size is sold out"); return; }
    addToCart(product, size, qty, hasColors ? color : "");
  };

  // Colour variants: each colour carries its own stock and (optional) photos.
  const variants = product.color_variants || [];
  const hasColors = variants.length > 0;
  const selectedVariant = hasColors ? (variants.find((v) => v.color === color) || variants[0]) : null;
  const colorStock = selectedVariant ? Number(selectedVariant.stock || 0) : 0;
  const galleryImages = (selectedVariant && selectedVariant.images && selectedVariant.images.length)
    ? selectedVariant.images
    : product.images;

  // Per-size stock: return the specific size bucket if the product tracks it,
  // otherwise the flat stock (so legacy products behave normally).
  const sizeStock = (s) => {
    if (hasColors) return colorStock;
    const sbs = product.stock_by_size;
    if (sbs && Object.keys(sbs).length > 0) return Number(sbs[s] || 0);
    return Number(product.stock || 0);
  };

  const totalStock = (() => {
    if (hasColors) return variants.reduce((a, v) => a + Number(v.stock || 0), 0);
    const sbs = product.stock_by_size;
    if (sbs && Object.keys(sbs).length > 0) {
      return Object.values(sbs).reduce((a, b) => a + Number(b || 0), 0);
    }
    return Number(product.stock || 0);
  })();

  const isFullySoldOut = totalStock <= 0;
  const selectedSizeOOS = hasColors ? (colorStock <= 0) : (size && sizeStock(size) <= 0);

  const submitNotify = async () => {
    if (!notifyEmail || !notifyEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    try {
      await api.post(`/products/${product.id}/notify`, { email: notifyEmail, size: size || "" });
      toast.success("We'll email you when this is back in stock");
      setNotifyMode(false); setNotifyEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
  };

  return (
    <div className="pt-20" data-testid="product-detail-page">
      <div className="px-5 md:px-10 pt-8 pb-4 max-w-[1600px] mx-auto">
        <div className="text-xs text-ink-soft mono">
          <Link to="/" className="hover:text-ink">Home</Link> / <Link to={`/shop?category=${product.category}`} className="hover:text-ink capitalize">{product.category}</Link> / <span className="text-ink">{product.name}</span>
        </div>
      </div>

      <div className="px-5 md:px-10 max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 pb-24">
        {/* gallery */}
        <div className="grid grid-cols-[70px_1fr] gap-4">
          <div className="flex flex-col gap-3">
            {galleryImages.map((img, i) => (
              <button key={i} onClick={() => setActive(i)} className={`aspect-[3/4] overflow-hidden border ${active === i ? "border-ink" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                <img src={img} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          <div className="relative group/edit" data-testid="pdp-gallery">
            <Zoom src={galleryImages[active] || galleryImages[0]} alt={product.name} />
            {isAdmin && (
              <button
                type="button"
                onClick={() => { setImgDraft(product.primary_image); setImgEditOpen(true); }}
                className="absolute top-3 right-3 z-30 opacity-0 group-hover/edit:opacity-100 focus:opacity-100 transition-opacity bg-white/95 hover:bg-white text-ink shadow-sm px-3 py-1.5 flex items-center gap-1.5 text-[0.65rem] tracking-[0.2em] uppercase"
                data-testid="pdp-image-edit"
              >
                <Pencil size={11} strokeWidth={1.6} /> Edit image
              </button>
            )}
          </div>
        </div>

        {isAdmin && (
          <Dialog open={imgEditOpen} onOpenChange={setImgEditOpen}>
            <DialogContent className="max-w-lg bg-white">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Edit product image</DialogTitle>
                <DialogDescription className="text-xs text-ink-soft">
                  Upload a new primary image. Recommended 1200 × 1600 (3:4 portrait).
                </DialogDescription>
              </DialogHeader>
              <ImageUpload label="" value={imgDraft} onChange={setImgDraft} />
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setImgEditOpen(false)}
                  className="overline px-4 py-2.5 border border-line hover:border-ink"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!imgDraft || imgDraft === product.primary_image) { setImgEditOpen(false); return; }
                    const newImages = [imgDraft, ...(product.images || []).slice(1)];
                    await savePatch({ primary_image: imgDraft, images: newImages });
                    toast.success("Product image updated");
                    setImgEditOpen(false);
                  }}
                  className="btn-ink px-6 py-2.5 overline"
                  data-testid="pdp-image-save"
                >
                  Save
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* info */}
        <div className="lg:pt-4">
          <FadeUp>
            <p className="overline text-gold-deep mb-3">{product.category} · {product.occasion}</p>
            <EditableText
              as="h1"
              value={product.name}
              onSave={async (v) => { await savePatch({ name: v }); toast.success("Name updated"); }}
              label="Product Name"
              className="font-serif text-4xl md:text-6xl tracking-tight leading-none"
              data-testid="pdp-name"
            >
              {product.name}
            </EditableText>
            <div className="mt-4 flex items-center gap-4">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className={i < Math.round(product.rating) ? "fill-gold text-gold" : "text-line"} />
                ))}
                <span className="text-xs text-ink-soft ml-1">{product.rating} ({product.reviews_count})</span>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-4">
              <EditableText
                as="span"
                value={product.price}
                inputType="number"
                onSave={async (v) => { await savePatch({ price: Number(v) }); toast.success("Price updated"); }}
                label="Price (₹)"
                className="text-2xl"
                data-testid="pdp-price"
              >
                {inr(product.price)}
              </EditableText>
              {product.compare_at_price && <span className="text-lg text-ink-soft line-through">{inr(product.compare_at_price)}</span>}
            </div>

            {/* colour */}
            {hasColors && (
              <div className="mt-8" data-testid="pdp-colors">
                <div className="flex items-center justify-between mb-3">
                  <p className="overline">Colour</p>
                  <span className="overline text-ink-soft">{color}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {variants.map((v) => {
                    const oos = Number(v.stock || 0) <= 0;
                    const thumb = (v.images && v.images[0]) || product.primary_image;
                    return (
                      <button
                        key={v.color}
                        onClick={() => { setColor(v.color); setActive(0); }}
                        className={`relative h-14 w-14 overflow-hidden border transition-colors ${color === v.color ? "border-ink ring-1 ring-ink" : "border-line hover:border-ink"} ${oos ? "opacity-50" : ""}`}
                        data-testid={`color-${v.color}`}
                        title={oos ? `${v.color} — sold out` : `${v.color} · ${v.stock} in stock`}
                      >
                        <img src={thumb} alt={v.color} className="h-full w-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* size */}
            <div className="mt-8">
              <div className="flex items-center justify-between mb-3">
                <p className="overline">Select Size</p>
                <span className="overline text-gold-deep">Size Guide</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => {
                  const stock = sizeStock(s);
                  const oos = stock <= 0;
                  return (
                    <button
                      key={s}
                      onClick={() => setSize(s)}
                      className={`relative h-12 w-14 border text-sm transition-colors ${
                        size === s
                          ? "border-ink bg-ink text-ivory"
                          : oos
                            ? "border-line text-ink-soft/60 bg-[#f2efe9] line-through cursor-pointer"
                            : "border-line hover:border-ink"
                      }`}
                      data-testid={`size-${s}`}
                      title={oos ? "Sold out" : `${stock} in stock`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              {size && (
                <p className={`mt-2 overline ${selectedSizeOOS ? "text-red-500" : "text-ink-soft"}`}>
                  {selectedSizeOOS ? "Sold out — get notified below" : `${sizeStock(size)} left in stock`}
                </p>
              )}
            </div>

            {/* qty + add */}
            <div className="mt-8 flex items-center gap-4">
              {!(selectedSizeOOS || isFullySoldOut) && (
                <div className="flex items-center border border-line">
                  <button className="px-3.5 py-3.5" onClick={() => setQty(Math.max(1, qty - 1))}><Minus size={13} /></button>
                  <span className="w-10 text-center text-sm">{qty}</span>
                  <button className="px-3.5 py-3.5" onClick={() => setQty(qty + 1)}><Plus size={13} /></button>
                </div>
              )}
              {selectedSizeOOS || (isFullySoldOut && !size) ? (
                <button
                  onClick={() => setNotifyMode(!notifyMode)}
                  className="btn-ink flex-1 py-4 overline bg-ink-soft"
                  data-testid="notify-me-button"
                >
                  {notifyMode ? "Close" : (isFullySoldOut && !size ? "Sold out · Notify me" : "Notify me when back")}
                </button>
              ) : (
                <button onClick={handleAdd} className="btn-ink flex-1 py-4 overline" data-testid="add-to-cart-button">Add to Bag</button>
              )}
              <button onClick={() => toggleWishlist(product)} className="grid h-[52px] w-[52px] place-items-center border border-line hover:border-ink transition-colors" data-testid="pdp-wishlist">
                <Heart size={17} fill={saved ? "currentColor" : "none"} />
              </button>
            </div>

            {notifyMode && (
              <div className="mt-4 p-4 bg-[#f2efe9] border border-line" data-testid="notify-form">
                <p className="overline text-ink-soft mb-2">Get an email when {size ? `size ${size}` : "this piece"} is back</p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    autoFocus
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 bg-white border border-line px-3 py-2.5 text-sm"
                    data-testid="notify-email"
                  />
                  <button onClick={submitNotify} className="btn-ink px-5 py-2.5 overline" data-testid="notify-submit">Notify me</button>
                </div>
              </div>
            )}

            {/* assurances */}
            <div className="mt-8 grid grid-cols-3 gap-4 border-y border-line py-6">
              {[[Truck, "Insured worldwide delivery"], [RefreshCw, "Easy 7-day returns"], [ShieldCheck, "Authenticity assured"]].map(([Icon, t], i) => (
                <div key={i} className="flex flex-col items-center text-center gap-2">
                  <Icon size={18} className="text-gold-deep" />
                  <span className="text-[0.7rem] text-ink-soft leading-tight">{t}</span>
                </div>
              ))}
            </div>

            {/* description (below Add to Bag) — inline editable for admins */}
            {(product.description || isAdmin) && (
              <div className="mt-8 pt-2" data-testid="pdp-description">
                <div className="flex items-center justify-between mb-3">
                  <p className="overline">Description</p>
                  {isAdmin && (
                    <EditableText
                      as="span"
                      value={product.description || ""}
                      multiline
                      onSave={async (v) => { await savePatch({ description: v }); toast.success("Description updated"); }}
                      label="Description"
                      className="overline text-gold-deep"
                    >
                      Edit
                    </EditableText>
                  )}
                </div>
                {product.description
                  ? <RichText text={product.description} className="text-sm text-ink-soft" />
                  : <p className="text-xs text-ink-soft italic">No description yet — click Edit to add one.</p>}
              </div>
            )}

            {/* accordion */}
            <Accordion type="single" collapsible className="mt-4" defaultValue="fabric">
              <AccordionItem value="fabric" className="border-line">
                <AccordionTrigger className="overline hover:no-underline" data-testid="acc-fabric">Fabric & Composition</AccordionTrigger>
                <AccordionContent className="text-sm text-ink-soft leading-relaxed">{product.fabric}. Hand-embellished with zardozi and sequin detailing. Comes with matching dupatta and inner lining.</AccordionContent>
              </AccordionItem>
              <AccordionItem value="care" className="border-line">
                <AccordionTrigger className="overline hover:no-underline">Care Instructions</AccordionTrigger>
                <AccordionContent className="text-sm text-ink-soft leading-relaxed">{product.care}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="delivery" className="border-line">
                <AccordionTrigger className="overline hover:no-underline">Delivery & Returns</AccordionTrigger>
                <AccordionContent className="text-sm text-ink-soft leading-relaxed">{product.delivery}</AccordionContent>
              </AccordionItem>
            </Accordion>
          </FadeUp>
        </div>
      </div>

      {/* reviews */}
      {(product.reviews_enabled !== false || isAdmin) && (
      <div className="bg-[#f2efe9] py-20">
        <div className="px-5 md:px-10 max-w-[1200px] mx-auto">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Reviews</h2>
            {isAdmin && (
              <button
                onClick={() => savePatch({ reviews_enabled: product.reviews_enabled === false })}
                className={`text-xs px-4 py-2 rounded-full border transition-colors ${product.reviews_enabled === false ? "border-line text-ink-soft" : "border-gold-deep text-gold-deep"}`}
                data-testid="toggle-reviews"
              >
                {product.reviews_enabled === false ? "Reviews hidden — click to show" : "Reviews shown — click to hide"}
              </button>
            )}
          </div>

          {isAdmin ? (
            <div className="space-y-4">
              {reviews.map((r, i) => (
                <div key={i} className="border border-line bg-white/60 rounded-lg p-4" data-testid={`review-edit-${i}`}>
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <button key={j} onClick={() => saveReviews(reviews.map((x, k) => k === i ? { ...x, rating: j + 1 } : x))}>
                        <Star size={16} className={j < r.rating ? "fill-gold text-gold" : "text-line"} />
                      </button>
                    ))}
                  </div>
                  <input value={r.title || ""} onChange={(e) => setReviews(reviews.map((x, k) => k === i ? { ...x, title: e.target.value } : x))} onBlur={() => saveReviews(reviews)} placeholder="Title" className="w-full font-serif text-xl bg-transparent border-b border-line py-1 mb-1 outline-none" data-testid={`review-title-${i}`} />
                  <textarea value={r.body || ""} onChange={(e) => setReviews(reviews.map((x, k) => k === i ? { ...x, body: e.target.value } : x))} onBlur={() => saveReviews(reviews)} placeholder="Review text" rows={2} className="w-full text-sm bg-transparent border-b border-line py-1 outline-none resize-none" data-testid={`review-body-${i}`} />
                  <div className="flex items-center justify-between mt-2">
                    <input value={r.name || ""} onChange={(e) => setReviews(reviews.map((x, k) => k === i ? { ...x, name: e.target.value } : x))} onBlur={() => saveReviews(reviews)} placeholder="Reviewer name" className="overline text-gold-deep bg-transparent outline-none" data-testid={`review-name-${i}`} />
                    <button onClick={() => saveReviews(reviews.filter((_, k) => k !== i))} className="text-xs text-red-600 hover:underline" data-testid={`review-delete-${i}`}>Delete</button>
                  </div>
                </div>
              ))}
              <button onClick={() => saveReviews([...reviews, { name: "", rating: 5, title: "", body: "" }])} className="text-sm px-4 py-2 border border-gold-deep text-gold-deep rounded-full hover:bg-gold-deep hover:text-white transition-colors" data-testid="review-add">+ Add review</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {reviews.map((r, i) => (
                <div key={i} className="border-b border-line pb-6" data-testid={`review-${i}`}>
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star key={j} size={13} className={j < r.rating ? "fill-gold text-gold" : "text-line"} />
                    ))}
                  </div>
                  <h4 className="font-serif text-xl">{r.title}</h4>
                  <p className="text-sm text-ink-soft mt-2 leading-relaxed">{r.body}</p>
                  <p className="overline text-gold-deep mt-3">— {r.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* related */}
      {product.related?.length > 0 && (
        <section className="px-5 md:px-10 py-24 max-w-[1600px] mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-10">You may also love</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 md:gap-x-6">
            {product.related.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} onQuickView={openQuickView} />
            ))}
          </div>
        </section>
      )}

      {/* recently viewed */}
      {recent.length > 0 && (
        <section className="px-5 md:px-10 pb-24 max-w-[1600px] mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-8">Recently viewed</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 md:gap-x-6">
            {recent.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} onQuickView={openQuickView} />
            ))}
          </div>
        </section>
      )}

      <QuickView product={quickView} open={qvOpen} onOpenChange={setQvOpen} />
    </div>
  );
}
